import { useMemo, useRef, useState } from 'react'
import { Button, Card, Field, FieldGrid, Input, SegmentedControl } from '../../../ui'
import {
    buildStaffEmailSignatureHtml,
    suggestEmailFromName,
    type StaffEmailSignatureTheme,
} from './buildSignatureHtml'
import styles from './styles.module.scss'

const THEME_OPTIONS: { value: StaffEmailSignatureTheme; label: string }[] = [
    { value: 'light', label: 'Light' },
    { value: 'dark', label: 'Dark' },
    { value: 'minimal', label: 'Minimal' },
]

export const StaffEmailSignatureView = () => {
    const [fullName, setFullName] = useState('')
    const [jobTitle, setJobTitle] = useState('')
    const [email, setEmail] = useState('')
    const [emailTouched, setEmailTouched] = useState(false)
    const [theme, setTheme] = useState<StaffEmailSignatureTheme>('light')
    const [status, setStatus] = useState('')
    const previewRef = useRef<HTMLDivElement>(null)

    const signatureHtml = useMemo(
        () => buildStaffEmailSignatureHtml({ fullName, jobTitle, email, theme }),
        [fullName, jobTitle, email, theme]
    )

    const onNameChange = (value: string) => {
        setFullName(value)
        if (!emailTouched) setEmail(suggestEmailFromName(value))
    }

    const copySignature = async () => {
        const node = previewRef.current
        if (!node) return

        try {
            await navigator.clipboard.write([
                new ClipboardItem({
                    'text/html': new Blob([node.innerHTML], { type: 'text/html' }),
                    'text/plain': new Blob([node.innerText], { type: 'text/plain' }),
                }),
            ])
            setStatus('Copied — paste into your email client signature settings.')
        } catch {
            const range = document.createRange()
            range.selectNodeContents(node)
            const selection = window.getSelection()
            selection?.removeAllRanges()
            selection?.addRange(range)
            const ok = document.execCommand('copy')
            setStatus(
                ok
                    ? 'Copied — paste into your email client signature settings.'
                    : 'Selected — press Cmd/Ctrl+C to copy.'
            )
        }
    }

    return (
        <div className={styles.layout}>
            <Card title="Your details" subtitle="Name and title appear on the signature. Email is suggested from your first name.">
                <div className={styles.fields}>
                    <Field label="Style" span="all">
                        <SegmentedControl
                            aria-label="Signature style"
                            className={styles.themeToggle}
                            options={THEME_OPTIONS}
                            value={theme}
                            onChange={setTheme}
                        />
                    </Field>
                    <FieldGrid minColumn={240}>
                        <Field label="Full name" required span="all">
                            {(id) => (
                                <Input
                                    id={id}
                                    value={fullName}
                                    onChange={(e) => onNameChange(e.target.value)}
                                    placeholder="John Smith"
                                    autoComplete="name"
                                />
                            )}
                        </Field>
                        <Field label="Job title" required span="all">
                            {(id) => (
                                <Input
                                    id={id}
                                    value={jobTitle}
                                    onChange={(e) => setJobTitle(e.target.value)}
                                    placeholder="Solicitor"
                                    autoComplete="organization-title"
                                />
                            )}
                        </Field>
                        <Field label="Email" required span="all" hint="Edit if your address is not firstname@sentinellegal.co.uk">
                            {(id) => (
                                <Input
                                    id={id}
                                    type="email"
                                    value={email}
                                    onChange={(e) => {
                                        setEmailTouched(true)
                                        setEmail(e.target.value)
                                    }}
                                    placeholder="john@sentinellegal.co.uk"
                                    autoComplete="email"
                                />
                            )}
                        </Field>
                    </FieldGrid>
                    <div className={styles.actions}>
                        <Button type="button" onClick={copySignature}>
                            Copy signature
                        </Button>
                        <p className={`${styles.status} ${status ? styles.statusOk : ''}`}>{status}</p>
                    </div>
                </div>
            </Card>

            <Card
                title="Preview"
                subtitle={`What gets copied into Gmail, Apple Mail, or Outlook (${theme} style).`}
            >
                <p className={styles.hint}>Website, address and SRA notice are fixed company details.</p>
                <div className={styles.previewShell}>
                    <div ref={previewRef} dangerouslySetInnerHTML={{ __html: signatureHtml }} />
                </div>
            </Card>
        </div>
    )
}
